import './style.css';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, ModalSize } from '@economic/gaudi';
import { analytics, UnexpectedError } from '@economic/react-app';
import { t, useLocalization } from '../../../shared/components/l10n/l10n';
import useCachedQuery from '../../../shared/hooks/useCachedQuery';
import internalApiClient from '../../../shared/util/api/internalApiClient';
import ChoosePlanPage from './ChoosePlanPage';
import { ConfirmPlanChangePage } from './ConfirmPlanChangePage';
import LoadingBox from './LoadingBox';
import { PlanSuccessPage } from './PlanSuccessPage';
import { PlanInfo, UpgradePlanInfo, UpgradeToPlanResult } from './shared';

function downloadDpaAgreement() {
    internalApiClient
        .get('/company/terms')
        .then(result => {
            window.location.assign(result.data.dpaPdfUrl);
        })
        .catch(() => {
            // ignore for now
        });
}

interface Props {
    hide: () => void;
    setSize: (size: ModalSize) => void;
}

function PlanUpgradeDialog({ hide, setSize }: Props) {
    // just a thought, use reducer...
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [step, setStep] = useState(0);
    const [selectedPlan, setSelectedPlan] = useState<PlanInfo | undefined>();
    const [result, setResult] = useState<UpgradeToPlanResult | undefined>();

    const upgradeQuery = useCachedQuery<UpgradePlanInfo>('home/upgrade');
    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        setSize('lg');
    }, []);

    if (upgradeQuery.loading || loadingTexts || isUpgrading) {
        return <LoadingBox />;
    }

    if (upgradeQuery.error) {
        return <UnexpectedError />;
    }

    function selectPlan(plan: any) {
        setSelectedPlan(plan);
        setStep(1);
        setSize('sm');
    }

    function cancelConfirmUpgrade() {
        setStep(0);
        setSize('lg');
    }

    function upgradeToSelectedPlan() {
        setIsUpgrading(true);

        internalApiClient
            .post<UpgradeToPlanResult>(`home/upgrade/${selectedPlan!.id}`)
            .then(r => {
                setResult(r.data);
                setIsUpgrading(false);
                setStep(2);
                setSize('sm');
                analytics.gtm('trial upgraded', {
                    gaCategory: 'subscription',
                    gaAction: 'package upgraded',
                    packageChangeDestination: selectedPlan?.name,
                    gaLabel: selectedPlan?.name,
                });
            })
            .catch(() => {
                toast.error(t('TOAST_SUBSCRIPTION_UPGRADE_ERROR'));
                hide();
            });
    }

    function close() {
        hide();
        window.top?.refreshRouting();
        navigate('/');
    }

    if (step === 1 && !!selectedPlan) {
        return (
            <ConfirmPlanChangePage
                selectedPlan={selectedPlan}
                onUpgrade={upgradeToSelectedPlan}
                onCancel={cancelConfirmUpgrade}
            />
        );
    }

    if (step === 2 && !!result) {
        return <PlanSuccessPage result={result} onClose={close} />;
    }

    return (
        <ChoosePlanPage
            data={upgradeQuery.data}
            selectPlan={selectPlan}
            downloadDpaAgreement={downloadDpaAgreement}
            hide={hide}
        />
    );
}

const textIds = [
    'SUBSCRIPTIONSTATUS_SIDEBAR_MAXTRANSACTIONSPERYEAR2',
    'SUBSCRIPTIONSTATUS_READSUBSCRIPTIONTERMSHERE',
    'SUBSCRIPTIONSTATUS_UPGRADETRIAL_HEADLINE',
    'SUBSCRIPTIONSTATUS_UPGRADETRIAL_SUBHEADLINE1',
    'SUBSCRIPTIONSTATUS_UPGRADETRIAL_SUBHEADLINE2',
    'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_HEADLINE',
    'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_SUBHEADLINE1',
    'SUBSCRIPTIONSTATUS_UPGRADEEXPIREDTRIAL_SUBHEADLINE2',
    'SUBSCRIPTIONSTATUS_PLANSTATUS_SELECT',
    'TERMSCONDITIONS_SUBSCRIPTION_PAGE_EDIT_DPA',
    'TERMSCONDITIONS_SUBSCRIPTION_PAGE_DOWNLOAD_DPA',
    'FORM_LICENSESTATUS_INVOICEPAIDVIALS',
    'SUBSCRIPTIONSTATUS_UPGRADE_CONFIRM_TITLE',
    'SUBSCRIPTIONSTATUS_UPGRADE_CONFIRM_MESSAGE',
    'SUBSCRIPTIONSTATUS_UPGRADE_CONFIRM_ACCEPT',
    'SUBSCRIPTIONSTATUS_UPGRADE_CONFIRM_CANCEL',
    'TOAST_SUBSCRIPTION_UPGRADE_ERROR',
    'SUBSCRIPTIONSTATUS_UPGRADE_FAILED_CONTENT',
    'SUBSCRIPTIONSTATUS_FINISHED_HURRA',
    'SUBSCRIPTIONSTATUS_FINISHED_CONTINUE',
    'SUBSCRIPTIONSTATUS_MARKETPANEL_TEXT',
    'SUBSCRIPTIONSTATUS_MARKETPANEL_READMORELINK',
    'SUBSCRIPTIONSTATUS_SEEPRICING',
    'TOAST_SUBSCRIPTION_TRIAL_EXTEND_SUCCESS',
    'TOAST_SUBSCRIPTION_TRIAL_EXTEND_ERROR',
    'SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_TEXT',
    'SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_CONFIRMATION_TITLE',
    'SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_CONFIRMATION_TEXT',
    'SUBSCRIPTIONSTATUS_TRIALEXPIRED_EXTEND_BUTTON',
];

export default PlanUpgradeDialog;
